import { parseJwt, type IAppleClientParams, type OauthUserInfo } from '@halo/webauth';
import { isPlainObject } from 'lodash-es';
import { safeJsonParse } from '@halo/utils';
import { AppleOauth } from '@halo/webauth/appleOauth';
import { WalletCore } from '@halo/wallet-app';
import { asymmetricEncryption } from '../walletCloud/helper';
import type {
  Account,
  uploadArgs,
  SaveAccountArgs,
  DeleteAccountArgs,
  Cloud,
  AuthInfo,
  AppleInitClientArgs,
  DeleteCloudDataArgs,
} from './types';
import { StatusCode, CloudPlatform } from './types';
import { deleteIcloudCookie, formatAccountsData, formatWalletData, getMetadata } from './helper';
import { EventBus } from './eventBus';
import { EVENT_NAME } from './eventBus/type';

const watchWindowProperty = (reject: any) => {
  let timer = 0;
  let isAuth = false;
  new EventBus().bind({
    eventName: EVENT_NAME.CLOSE_CLOUD_KIT,
    callback: () => {
      isAuth = true;
    },
  });

  const checkIsWindowClosed = (win: Window, reject: any) => {
    if (timer) {
      window.clearTimeout(timer);
    }
    if (win.closed) {
      const timer_ = window.setTimeout(() => {
        if (!isAuth) {
          reject({ error: 'cancel cloud outh' });
        }
        window.clearTimeout(timer_);
      }, 500);
      return;
    }
    timer = window.setTimeout(() => {
      checkIsWindowClosed(win, reject);
    }, 1000);
  };

  const originWindowOpen = window.open;
  window.open = function (...args) {
    const popupRes = originWindowOpen(...args);
    const origin = new URL([...args]?.[0] as string)?.origin;
    if (popupRes && origin === 'https://idmsa.apple.com') {
      checkIsWindowClosed(popupRes, reject);
    }
    return popupRes;
  };
};

const replaceDomNode = (
  parentNode: HTMLElement,
  excludeChildNode: HTMLElement,
  text?: string
) => {
  const fragment = document.createDocumentFragment();
  if (text) {
    fragment.appendChild(document.createTextNode(text));
  } else {
    for (
      let node = parentNode.firstChild;
      node && node !== excludeChildNode;
      node = node.nextSibling
    ) {
      if (node.nodeType === Node.TEXT_NODE) {
        const trimmed = node.textContent?.trim();
        if (trimmed) {
          fragment.appendChild(document.createTextNode(trimmed));
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        fragment.appendChild(node);
      }
    }
  }
  excludeChildNode.innerHTML = '';
  excludeChildNode.style = '';
  excludeChildNode.appendChild(fragment);
  parentNode.innerHTML = '';
  parentNode.appendChild(excludeChildNode);
};

export class AppleCloud implements Cloud {
  static client?: AppleOauth;
  readonly platform: CloudPlatform.appleCloud = CloudPlatform.appleCloud;
  // userInfo: OauthUserInfo | null = null;
  authInfo!: AuthInfo;
  key!: string;
  cid?: string = '';

  logout = (containerId: string) => {
    AppleCloud.client = undefined;
    deleteIcloudCookie(containerId);
  };

  private getMetadata = (subId?: string) => {
    return getMetadata({ key: this.key, subId });
  };

  cloudKitSetUpAuth = async () => {
    return await AppleCloud.client?.cloudKitSetUpAuth();
  };

  getUserInfoByIdToken = (id_token: string) => {
    const { email, sub } = parseJwt(id_token || '');
    return { email, id: sub, picture: '' };
  };

  requestAuthRedirect = (clientParams: any) => {
    const url = new URL(`https://appleid.apple.com/auth/authorize`);
    // const redirect_web_uri = 'http://localhost:7465/login';
    // url.searchParams.set('client_id', import.meta.env.VITE_CLIENT_ID_APPLE);
    // url.searchParams.set('redirect_uri', 'http://localhost:7465/api/v1/user/login/apple/callback');
    // url.searchParams.set('response_type', 'code');
    // url.searchParams.set('response_mode', 'form_post');
    // url.searchParams.set('scope', 'email name');
    // url.searchParams.set('state', encodeURIComponent(redirect_web_uri));

    const clientParams_ = {
      scope: 'email name',
      response_type: 'code',
      response_mode: 'form_post',
      ...clientParams,
    };

    for (const key in clientParams_) {
      url.searchParams.set(key, clientParams_[key]);
    }
    window.location.href = url.toString();
  };

  requestAccountToken = async ({
    oauthClient,
    clientParams,
  }: {
    oauthClient: AppleOauth;
    clientParams: any;
  }) => {
    const { requestToken } = clientParams;
    const res = await oauthClient.signIn();
    const { authorization, user } = res;
    if (authorization?.code) {
      const params: any = {
        platform: CloudPlatform.appleCloud,
        // code: authorization?.code,
        // id_token: authorization?.id_token,
        ...authorization,
      };
      if (user) {
        params.user = user;
      }
      if (requestToken) {
        return await requestToken(params);
      } else {
        return params;
      }
    }
  };

  initClient = (args: AppleInitClientArgs): Promise<AuthInfo> => {
    const { key, cid, authInfo, clientParams } = args || {};
    this.key = key;
    this.cid = cid;
    return new Promise((resolve, reject) => {
      const fn = async () => {
        const { redirect_uri, client_id, ...restClientParams } = clientParams;
        const clientParams_ = {
          usePopup: true,
          //scope: "name email",
          scope: 'name',
          environment: 'production',
          ...restClientParams,
          clientId: client_id,
          redirectURI: redirect_uri,
        };
        const oauthClient = new AppleOauth();
        await oauthClient.init(clientParams_ as IAppleClientParams);

        if (authInfo?.access_token) {
          if (clientParams_.usePopup || clientParams_.ckWebAuthToken) {
            this.authInfo = {
              ...authInfo,
              ckWebAuthToken: clientParams_.ckWebAuthToken,
            };
          }
          AppleCloud.client = oauthClient;
          resolve(this.authInfo);
          return;
        }

        try {
          // apple
          if (clientParams_.signInButton?.id) {
            const signInDom = document.getElementById(clientParams_.signInButton.id);
            if (!signInDom) {
              throw new Error('SignInDom not found');
            }
            signInDom?.addEventListener(
              'click',
              () => {
                clientParams_.signInButton?.onOk?.();
              },
              { once: true }
            );
          }

          if (clientParams_.signOutButton?.id) {
            const signOutDom = document.getElementById(clientParams_.signOutButton.id);
            if (!signOutDom) {
              throw new Error('SignOutDom not found');
            }
            // if (clientParams_.signOutButton?.text) {
            // 	const signOutDom = document.getElementById(clientParams_.signOutButton.id);
            // 	signOutDom!.innerHTML = clientParams_.signOutButton.text;
            // }
            signOutDom?.addEventListener(
              'click',
              () => {
                clientParams_.signOutButton?.onCancel?.();
                throw new Error('User cancel');
              },
              { once: true }
            );
          }

          let accountTokenInfo = null;
          if (!authInfo?.access_token) {
            accountTokenInfo = await this.requestAccountToken({
              oauthClient,
              clientParams: clientParams_,
            });
          }

          // cloudKit
          const authRes = await oauthClient.cloudKitSetUpAuth().catch((err) => {
            console.log(err, 'cloudKitSetUpAuth err');
            return err;
          });
          if (!authRes?.isLoginCloud) {
            // TODO clientParams_.redirect_uri
            if (authRes.redirectURL) {
              location.href = authRes.redirectURL;
            } else {
              // if (clientParams_.signInButton?.text) {
              // 	const signInDom = document.getElementById(clientParams_.signInButton.id);
              // 	if (!signInDom) {
              // 		throw new Error("SignInDom not found");
              // 	}
              // 	const authButtonDom = signInDom!.getElementsByClassName('apple-auth-button')?.[0];

              // 	if (authButtonDom) {
              // 		replaceDomNode(signInDom, authButtonDom, clientParams_.signInButton?.text);
              // 		// authButtonDom.innerHTML = clientParams_.signInButton.text;
              // 		// authButtonDom.style = '';
              // 		// signInDom.innerHTML = ''
              // 		// signInDom.appendChild(authButtonDom);
              // 	}
              // }
              if (clientParams_.signInButton?.id) {
                const signInDom = document.getElementById(clientParams_.signInButton.id);
                if (!signInDom) {
                  throw new Error('SignInDom not found');
                }
                const authButtonDom = signInDom!.getElementsByClassName(
                  'apple-auth-button'
                )?.[0] as HTMLElement;

                if (authButtonDom) {
                  replaceDomNode(signInDom, authButtonDom, clientParams_.signInButton?.text);
                }
              }
              watchWindowProperty(reject);

              if (clientParams_?.onPopup) {
                await clientParams_.onPopup();
              }

              await oauthClient.authCloudKit();
              new EventBus().publish(EVENT_NAME.CLOSE_CLOUD_KIT);
              if (clientParams_.signOutButton?.id) {
                const signOutDom = document.getElementById(clientParams_.signOutButton.id);
                if (!signOutDom) {
                  throw new Error('SignOutDom not found');
                }
                const authButtonDom = signOutDom!.getElementsByClassName(
                  'apple-auth-button'
                )?.[0] as HTMLElement;
                if (authButtonDom) {
                  replaceDomNode(signOutDom, authButtonDom, clientParams_.signOutButton?.text);
                }
              }
            }
          }

          this.authInfo = {
            ...accountTokenInfo,
            access_token: accountTokenInfo.id_token,
          };
          AppleCloud.client = oauthClient;
          resolve(this.authInfo);
        } catch (err: any) {
          if (err?.error === 'popup_closed_by_user') {
            reject({ code: StatusCode.OUTH_POPUP_CANCEL, message: 'popup_closed' });
          } else if (err?.error === 'user_cancelled_authorize') {
            reject({ code: StatusCode.OUTH_CLOUD_CANCEL, message: 'user_cancelled_authorize' });
          }
          console.log(err, 'err');
          reject(err);
        }
      };
      fn();
    });
  };
  private readonly errorInterception = <Params, Response>(
    callback: (...args: Params[]) => Promise<Response>
  ) => {
    return async (...args: Params[]) => {
      try {
        return await callback(...args);
      } catch (err: any) {
        const error = safeJsonParse(err.message || err, err.message);
        console.error('errorInterception', error, err);
        if (err?.error === 'popup_closed_by_user') {
          //throw i18next.t('common:userReject');
        }
        if (err._serverErrorCode === 'QUOTA_EXCEEDED') {
          throw { code: StatusCode.OUTH_UN_QUOTA, message: 'Quota exceeded' };
        }
        throw err;
      }
    };
  };
  getUserInfo = this.errorInterception<void, OauthUserInfo>(async () => {
    if (!this.authInfo) {
      throw Error(JSON.stringify({ code: 401, message: 'unauthorized' }));
    }
    return this.authInfo.id_token
      ? this.getUserInfoByIdToken(this.authInfo.id_token)
      : await AppleCloud.client!.getUserInfo();
  });

  upload = this.errorInterception<uploadArgs, any>(async (args: uploadArgs) => {
    const { isOldVersion, data } = args;
    if (!this.cid) {
      const userInfo = await this.getUserInfo();
      this.cid = asymmetricEncryption(userInfo.id!);
    }
    const metadata = this.getMetadata(isOldVersion ? undefined : this.cid);
    const fileName = metadata.name;
    if (!isPlainObject(data)) {
      throw Error('theDataShouldBeAnObjectType');
    }

    const file = new File([JSON.stringify(data)], fileName);

    const existFiles: any = await AppleCloud.client?.getDriveFiles(metadata);
    const privateFile: any = existFiles?.records[0];

    const updateRes = await AppleCloud.client?.uploadFile({
      metadata,
      fileId: privateFile?.recordChangeTag,
      file,
    });
    return updateRes;
  });

  updateWalletAccounts = this.errorInterception<any, any>(
    async (args: { isChange?: boolean; password?: string; accounts?: Account[]; t?: number }) => {
      const { isChange, password, accounts, t } = args;
      const oldData = await this.getCloudData();

      if (!oldData || JSON.stringify(oldData) === '{}') {
        throw Error('no data');
      }

      const oldWallet = oldData.wallet!;

      const isChange_ = isChange ?? !!oldWallet?.isChange;
      const password_ = isChange_ ? '' : password || oldWallet.password!;
      const accounts_ = accounts || oldWallet?.accounts;

      const newWalletData = await formatWalletData({
        ...oldWallet,
        isChange: isChange_,
        password: password_,
        accounts: accounts_,
      });

      const data = {
        ...oldData,
        wallet: t ? { ...newWalletData, t } : newWalletData,
      };

      await this.upload({
        data,
      });

      return {
        oldData: oldData.wallet as any,
        newData: data.wallet as any,
      };
    }
  );

  updateWalletAccount = async (args: { account: Partial<Account> }) => {
    const { account } = args;
    const oldData = await this.getCloudData();

    if (!oldData || JSON.stringify(oldData) === '{}') {
      throw Error('no data');
    }

    const oldWallet = oldData.wallet!;

    const { accounts } = oldWallet;

    const oldAccountIndex = accounts.findIndex((item: Account) => item.id === account.id);

    if (oldAccountIndex === -1) {
      throw Error('account not found');
    }

    const oldAccount = accounts[oldAccountIndex];

    accounts.splice(oldAccountIndex, 1, Object.assign(oldAccount, account));

    const data = {
      ...oldData,
      wallet: await formatWalletData({
        ...oldWallet,
        accounts,
      }),
    };

    await this.upload({
      data,
    });

    return {
      oldData: oldData.wallet as any,
      newData: data.wallet as any,
    };
  };

  saveWalletAccount = this.errorInterception<SaveAccountArgs, any>(
    async (args: SaveAccountArgs) => {
      const { account } = args;
      const oldData = await this.getCloudData();

      if (!oldData || JSON.stringify(oldData) === '{}') {
        throw Error('no data');
      }

      const oldWallet = oldData.wallet!;

      const data = {
        ...oldData,
        wallet: await formatWalletData({
          ...oldWallet,
          accounts: [...oldWallet.accounts, account],
        }),
      };

      await this.upload({
        data,
      });

      return {
        oldData: oldData.wallet as any,
        newData: data.wallet as any,
      };
    }
  );
  deleteWalletAccount = this.errorInterception<
    DeleteAccountArgs,
    {
      oldData: uploadArgs['data']['wallet'];
      newData: uploadArgs['data']['wallet'];
    }
  >(async (args: DeleteAccountArgs) => {
    const { id } = args;
    const oldData = await this.getCloudData();

    if (!oldData || JSON.stringify(oldData) === '{}') {
      throw Error('no data');
    }

    const oldWallet = oldData.wallet!;

    const data = {
      ...oldData,
      wallet: await formatWalletData({
        ...oldWallet,
        accounts: oldWallet?.accounts?.filter((item) => item.id !== id) || [],
      }),
    };

    await this.upload({ data });

    return {
      oldData: oldData.wallet as any,
      newData: data.wallet as any,
    };
  });
  deleteCloudData = this.errorInterception<
    DeleteCloudDataArgs,
    {
      oldData: uploadArgs['data'];
      newData: uploadArgs['data'];
    }
  >(async (args: DeleteCloudDataArgs) => {
    const { keys } = args ?? {};
    let oldData = await this.getCloudData();

    if (!oldData || JSON.stringify(oldData) === '{}') {
      throw Error('no data');
    }

    if (!keys?.length) {
      oldData = {};
    } else {
      for (const value of keys) {
        delete oldData[value];
      }
    }

    const data = {
      ...oldData,
    };

    await this.upload({ data });

    return {
      oldData: oldData,
      newData: data,
    };
  });

  getWalletAccounts = this.errorInterception<void, Account[]>(async () => {
    const data = await this.getCloudData();
    return data?.wallet?.accounts || [];
  });

  getPassword = this.errorInterception<void, { password?: string; isChange?: boolean }>(
    async () => {
      const oldData: any = await this.getCloudData();
      return {
        password: oldData?.password,
        isChange: oldData?.isChange,
      };
    }
  );

  getFileDownloadUrl = async (id?: string) => {
    const metadata = this.getMetadata(id);
    const existFiles = await AppleCloud.client?.getDriveFiles(metadata);
    const privateFile: any = existFiles?.records?.[0];
    const fileField = privateFile?.fields?.fileData;
    const fileURL = fileField?.value?.downloadURL;
    return fileURL;
  };

  getCloudData = this.errorInterception<{ cid: string }, Partial<uploadArgs['data']>>(
    async (params) => {
      let { cid } = params ?? { cid: this.cid };
      if (!cid) {
        const userInfo = await this.getUserInfo();
        cid = asymmetricEncryption(userInfo.id!);
      }
      this.cid = cid;

      let fileURL = await this.getFileDownloadUrl(cid);
      let isNewData = true;

      if (!fileURL) {
        fileURL = await this.getFileDownloadUrl();
        isNewData = false;
        if (!fileURL) {
          return {};
        }
      }

      const res = await AppleCloud.client?.getFileContent(fileURL);
      const data = typeof res === 'string' ? safeJsonParse(res, {}) : (res as any);
      if (!isNewData) {
        const newAccounts = WalletCore.migrateAccounts(data.accounts?.[cid!] || []);
        const newData = {
          wallet: await formatWalletData({
            ...data,
            accounts: newAccounts,
          }),
        };
        await this.upload({
          isOldVersion: true,
          data: {} as any,
        });
        await this.upload({
          data: newData,
        });
        return newData;
      }
      if (
        data?.wallet?.accounts?.length &&
        (data.wallet.accounts.some((account: any) => 'seriesWallets' in account) ||
          data?.wallet?.version === 1)
      ) {
        const newAccounts = WalletCore.migrateAccounts(data.wallet.accounts || []);

        const newInvalidAccounts = data.wallet?.invalidAccounts?.length
          ? WalletCore.migrateAccounts(data.wallet?.invalidAccounts)
          : [];

        const backupCloudData = safeJsonParse(data.wallet?.backupCloudData || '', {} as any);
        const newBackupCloudDataAccounts = backupCloudData?.accounts?.length
          ? WalletCore.migrateAccounts(backupCloudData.accounts)
          : [];

        const newWallet = await formatWalletData({
          ...data.wallet,
          accounts: newAccounts,
          invalidAccounts: newInvalidAccounts,
        });

        const newData = {
          ...data,
          wallet: {
            ...newWallet,
            backupCloudData: newBackupCloudDataAccounts.length
              ? JSON.stringify({
                  ...backupCloudData,
                  t: newWallet.t,
                  version: newWallet.version,
                  accounts: formatAccountsData(newBackupCloudDataAccounts),
                })
              : '',
          },
        };
        await this.upload({
          data: newData,
        });
        return newData;
      }
      return data;
    }
  );
}
